import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { Department, DepartmentMember } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { DescriptionList } from '@/app/components/ui/DescriptionList';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { RemoveDepartmentMemberButton } from '@/features/users/memberActions';
import { StatsGrid } from '@/features/statistics/StatsGrid';
import { departmentStatsPreset } from '@/features/statistics/presets';
import { useAsync } from '@/lib/useAsync';

export function DepartmentDetailPage() {
  const { t } = useTranslation();
  const { departmentId = '' } = useParams();
  const department = useAsync(
    () => apiGet('/departments/{departmentId}', { params: { departmentId } }),
    [departmentId],
  );

  return (
    <section className="page">
      <AsyncSection state={department} label={t('departments.detail.thisDepartment')}>
        {(data) => <DepartmentDetail department={data as Required<Department>} />}
      </AsyncSection>
    </section>
  );
}

function DepartmentDetail({ department }: { department: Required<Department> }) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const scope = { id: department.id, company_id: department.company_id ?? null };
  const showStats = permissions.canViewDepartmentStats(scope);
  const showSettings =
    permissions.canUpdateDepartment(scope) ||
    permissions.canSetVisibility ||
    permissions.canRecordCollectiveConsent ||
    permissions.canGrantRoles;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={department.name}
        actions={
          <>
            {permissions.canUpdateDepartment(scope) && (
              <Button as={Link} variant="secondary" to={`/departments/${department.id}/edit`}>
                {t('departments.detail.edit')}
              </Button>
            )}
            {permissions.canSetVisibility && (
              <Button as={Link} variant="secondary" to={`/departments/${department.id}/visibility`}>
                {t('departments.detail.setVisibility')}{' '}
                <Badge tone="outline">{t('accountMenu.roles.admin')}</Badge>
              </Button>
            )}
            {permissions.canRecordCollectiveConsent && (
              <Button
                as={Link}
                variant="secondary"
                to={`/departments/${department.id}/collective-consent`}
              >
                {t('departments.detail.collectiveConsent')}{' '}
                <Badge tone="outline">{t('accountMenu.roles.admin')}</Badge>
              </Button>
            )}
            {permissions.canGrantRoles && (
              <Button as={Link} variant="secondary" to={`/departments/${department.id}/roles`}>
                {t('departments.detail.coach')}
              </Button>
            )}
          </>
        }
      />

      {permissions.canViewDepartmentMembers(scope) && <DepartmentMembers scope={scope} />}

      {showStats && (
        <Card>
          <CardHeader title={t('departments.detail.statsCardTitle')} />
          <StatsGrid panels={departmentStatsPreset({ department_id: department.id })} />
        </Card>
      )}

      {showSettings && (
        <Card>
          <CardHeader title={t('departments.detail.settingsCardTitle')} />
          <DescriptionList
            items={[
              { label: t('departments.detail.category'), value: department.category ?? '—' },
              { label: t('departments.detail.type'), value: department.type ?? '—' },
              {
                label: t('departments.detail.statistics'),
                value: <Badge tone="outline">{department.stats_visibility}</Badge>,
              },
              {
                label: t('departments.detail.collectiveConsent'),
                value: department.collective_consent
                  ? t('departments.list.inForce')
                  : t('departments.detail.notRecorded'),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * The member list a coach or the owning company's admin may read. Shows
 * nickname, never an e-mail address — the API draws that privacy line and the
 * UI keeps it.
 */
function DepartmentMembers({ scope }: { scope: { id: string; company_id: string | null } }) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  // Removing a member is the company admin's call (or the admin's) — a
  // department admin only reads its list and provisions new people.
  const mayManage = scope.company_id !== null && permissions.canManageDepartments(scope.company_id);
  const members = useAsync(
    () =>
      apiGet('/departments/{departmentId}/users', {
        params: { departmentId: scope.id },
      }) as unknown as Promise<DepartmentMember[]>,
    [scope.id],
  );

  return (
    <Card padding="none" aria-label={t('departments.detail.membersCardTitle')}>
      <div className="p-5 pb-0">
        <CardHeader
          title={t('departments.detail.membersCardTitle')}
          action={
            permissions.canProvisionInDepartment(scope) ? (
              <Button as={Link} size="sm" to={`/departments/${scope.id}/members/new`}>
                {t('departments.detail.addPerson')}
              </Button>
            ) : undefined
          }
        />
      </div>
      <AsyncSection
        state={members}
        label={t('departments.detail.membersCardTitle')}
        skeleton="table"
      >
        {(rows) =>
          rows.length === 0 ? (
            <div className="p-5 pt-0">
              <EmptyState text={t('departments.detail.noMembers')} />
            </div>
          ) : (
            <DataTable
              rowKey={(m) => m.user_id}
              rows={rows}
              columns={[
                {
                  key: 'name',
                  header: t('departments.detail.name'),
                  render: (m) => <Link to={`/users/${m.user_id}`}>{m.name}</Link>,
                },
                {
                  key: 'nickname',
                  header: t('departments.detail.nickname'),
                  render: (m) => m.nickname ?? '—',
                },
                {
                  key: 'login',
                  header: t('departments.detail.login'),
                  render: (m) =>
                    m.has_login ? t('departments.detail.yes') : t('departments.detail.no'),
                },
                ...(mayManage
                  ? [
                      {
                        key: 'actions',
                        header: '',
                        align: 'end' as const,
                        render: (m: DepartmentMember) => (
                          <RemoveDepartmentMemberButton
                            departmentId={scope.id}
                            userId={m.user_id}
                            onDone={members.reload}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          )
        }
      </AsyncSection>
    </Card>
  );
}
